<script setup>
import { computed, ref, watch } from 'vue';
import { formatNumber } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ChatListSearchOverlay from 'dashboard/components/widgets/conversation/ChatListSearchOverlay.vue';
import {
  CONVERSATION_COLUMN_COMPACT_INSET_CLASS,
  CONVERSATION_COLUMN_INSET_CLASS,
} from './conversationColumnLayout';

const props = defineProps({
  pageTitle: { type: String, required: true },
  isWorkspaceV2: { type: Boolean, default: false },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  searchQuery: { type: String, default: '' },
  // Estado dos menus do cromo, que vivem no QuickFilterTabs mas são
  // DISPARADOS daqui (o header V3 é quem tem os ícones).
  filterActive: { type: Boolean, default: false },
  filterOpen: { type: Boolean, default: false },
  shortcutsOpen: { type: Boolean, default: false },
  isListCompact: { type: Boolean, default: false },
  // Segmento (Nao lidas / Pendentes / Minhas) vem pronto do ChatList: em
  // coluna estreita ele nao cabe como faixa, entao vira seletor aqui.
  segmentItems: { type: Array, default: () => [] },
  activeSegmentKey: { type: String, default: '' },
});

const emit = defineEmits([
  'selectSegment',
  'resetFilters',
  'filtersModal',
  'update:searchQuery',
  'toggleFilter',
  'toggleShortcuts',
  'toggleCompact',
]);

const isSegmentOpen = ref(false);
const segmentTriggerRef = ref(null);

// SiteUp: valor local do campo. Digitar nao espera o pai re-renderizar
// (quando o debounce da lista trava, o binding controlado comia caracteres).
const localSearchQuery = ref(props.searchQuery || '');

watch(
  () => props.searchQuery,
  value => {
    const next = value || '';
    if (next !== localSearchQuery.value) {
      localSearchQuery.value = next;
    }
  }
);

// v-model ja atualiza localSearchQuery; so propaga pro pai.
const onSearchInput = event => {
  emit('update:searchQuery', event.target.value);
};

const clearSearchQuery = () => {
  localSearchQuery.value = '';
  emit('update:searchQuery', '');
};

const activeSegmentItem = computed(
  () =>
    props.segmentItems.find(item => item.key === props.activeSegmentKey) ||
    props.segmentItems[0]
);
const activeSegmentIcon = computed(
  () => activeSegmentItem.value?.icon || 'i-ph-funnel'
);
const segmentTriggerLabel = computed(() => {
  const item = activeSegmentItem.value;
  if (!item) return '';
  return item.count ? `${item.label}: ${item.count}` : item.label;
});

const onPickSegment = key => {
  isSegmentOpen.value = false;
  emit('selectSegment', key);
};

// Em 256px a fileira inteira (seletor + 3 ícones) deixava 60px pra busca: só
// a lupa cabia. Em repouso mostra tudo; ao focar (ou com texto digitado) o
// resto recolhe e a busca fica com a linha toda.
const isSearchFocused = ref(false);
const isSearchExpanded = computed(
  () => isSearchFocused.value || Boolean(localSearchQuery.value)
);
// O estado aberto precisa de um max-width explícito: o browser não anima a
// partir de `none`. E `overflow-hidden` fixo cortaria o menu do seletor, que é
// absolute — por isso a classe só entra quando está recolhido de fato.
const collapsedClass = openWidth =>
  isSearchExpanded.value
    ? 'max-w-0 opacity-0 overflow-hidden pointer-events-none'
    : `${openWidth} opacity-100`;

// Manda o rect do botão junto: quem desenha o painel é o QuickFilterTabs, e
// sem a âncora ele não sabe onde se posicionar.
const onToggle = (which, event) => {
  const rect = event.currentTarget?.getBoundingClientRect?.() || null;
  if (which === 'filter') emit('toggleFilter', rect);
  else emit('toggleShortcuts', rect);
};

const { t } = useI18n();

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

// SiteUp: label do status no header. Status combinado (ex.: "open,snoozed",
// dos filtros configuráveis) não tem chave i18n própria — traduz cada status
// atômico e junta (ex.: "Aberto, Adiado").
const statusLabel = computed(() => {
  const status = props.activeStatus || '';
  if (status.includes(',')) {
    return (
      status
        .split(',')
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        .map(s => t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${s}.TEXT`))
        .join(', ')
    );
  }
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`);
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));
</script>

<template>
  <div
    class="md:border-b md:border-n-strong"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <!-- Compacto: só a busca. Filtro, atalhos e recolher ficam no confortável. -->
    <div
      v-if="isListCompact"
      class="flex items-center gap-0.5 py-1.5"
      :class="CONVERSATION_COLUMN_COMPACT_INSET_CLASS"
    >
      <!-- Seletor de aba junto da busca. Em coluna estreita a faixa de três
           abas comia uma linha inteira e ainda espremia ícone e número; aqui
           mostra só a aba ativa e abre as outras no clique. -->
      <div
        v-if="segmentItems.length"
        class="relative shrink-0 transition-[max-width,opacity] duration-200 motion-reduce:transition-none"
        :class="collapsedClass('max-w-[7rem]')"
      >
        <button
          ref="segmentTriggerRef"
          type="button"
          data-testid="chat-list-segment-trigger"
          class="inline-flex h-8 items-center gap-0.5 rounded-full px-1.5 text-[12px] font-semibold text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :class="{ 'bg-n-alpha-2 text-n-slate-12': isSegmentOpen }"
          :aria-expanded="isSegmentOpen"
          aria-haspopup="true"
          :aria-label="segmentTriggerLabel"
          :title="segmentTriggerLabel"
          @click="isSegmentOpen = !isSegmentOpen"
        >
          <span class="size-[18px] shrink-0" :class="activeSegmentIcon" />
          <span
            v-if="activeSegmentItem?.count"
            class="shrink-0 tabular-nums"
            aria-hidden="true"
          >
            {{ activeSegmentItem.count }}
          </span>
        </button>
        <div
          v-if="isSegmentOpen"
          v-on-clickaway="() => (isSegmentOpen = false)"
          data-testid="chat-list-segment-menu"
          role="listbox"
          class="absolute z-50 mt-1 flex min-w-[11rem] flex-col gap-0.5 rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg ltr:left-0 rtl:right-0"
        >
          <button
            v-for="item in segmentItems"
            :key="item.key"
            type="button"
            role="option"
            :aria-selected="item.key === activeSegmentKey"
            :data-testid="`chat-list-segment-option-${item.key}`"
            class="flex min-h-9 items-center gap-2 rounded-lg px-2 text-left text-[13px] transition-colors hover:bg-n-alpha-2"
            :class="
              item.key === activeSegmentKey
                ? 'font-semibold text-n-blue-11'
                : 'text-n-slate-12'
            "
            @click="onPickSegment(item.key)"
          >
            <span class="size-4 shrink-0" :class="item.icon" />
            <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
            <span
              v-if="item.count"
              class="shrink-0 text-[11px] tabular-nums text-n-slate-10"
            >
              {{ item.count }}
            </span>
          </button>
        </div>
      </div>
      <div class="relative min-w-0 flex-1">
        <span
          class="pointer-events-none absolute inset-y-0 flex items-center text-n-slate-10 ltr:left-2 rtl:right-2"
        >
          <span class="i-ph-magnifying-glass size-3.5" />
        </span>
        <input
          v-model="localSearchQuery"
          data-testid="conversation-list-search-input"
          type="search"
          :aria-label="$t('CHAT_LIST.SEARCH.INPUT')"
          :placeholder="$t('CHAT_LIST.SEARCH.INPUT_SHORT')"
          class="reset-base !mb-0 min-w-0 h-8 w-full appearance-none rounded-full border-0 bg-n-alpha-2 py-1.5 text-[13px] text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:bg-n-solid-2 focus:ring-1 focus:ring-n-brand ltr:pl-7 ltr:pr-6 rtl:pr-7 rtl:pl-6 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          @input="onSearchInput"
          @focus="isSearchFocused = true"
          @blur="isSearchFocused = false"
        />
        <button
          v-if="localSearchQuery"
          data-testid="conversation-list-search-clear"
          type="button"
          class="absolute inset-y-0 flex items-center px-1 text-n-slate-9 hover:text-n-slate-12 ltr:right-1.5 rtl:left-1.5"
          :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
          @click="clearSearchQuery"
        >
          <span class="i-ph-x size-3.5" />
        </button>
        <ChatListSearchOverlay
          :search-query="localSearchQuery"
          :anchor-open="isSearchExpanded"
        />
      </div>
    </div>

    <!-- Confortável: título + ações, busca numa segunda linha. -->
    <template v-else>
      <div
        class="flex min-h-[3.25rem] flex-wrap items-center justify-between gap-2 py-2"
        :class="CONVERSATION_COLUMN_INSET_CLASS"
      >
        <div class="flex min-w-0 basis-36 flex-1 items-center">
          <h1
            class="truncate text-xl font-medium tracking-tight text-n-slate-12"
            :title="pageTitle"
          >
            {{ pageTitle }}
          </h1>
          <span
            v-if="
              allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
            "
            class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
            :title="allCount"
          >
            {{ formattedAllCount }}
          </span>
          <span
            v-if="!hasAppliedFiltersOrActiveFolders"
            class="mx-1 my-0.5 shrink-0 rounded-md bg-n-slate-3 px-2 py-1 text-xxs capitalize text-n-slate-12"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div
          class="ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-1"
        >
          <button
            v-if="!isWorkspaceV2"
            v-tooltip.bottom="$t('CHAT_LIST.WORKBENCH.COLLAPSE')"
            type="button"
            data-testid="chat-list-workbench-toggle"
            class="inline-flex size-9 items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-pressed="false"
            :aria-label="$t('CHAT_LIST.WORKBENCH.COLLAPSE')"
            @click="emit('toggleCompact')"
          >
            <span class="i-ph-sidebar size-[18px]" />
          </button>
          <button
            v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
            type="button"
            data-testid="chat-list-filter"
            class="relative inline-flex items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="[
              isWorkspaceV2 ? 'size-11' : 'size-9',
              filterActive || filterOpen
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
            ]"
            :aria-expanded="filterOpen"
            aria-haspopup="dialog"
            :aria-label="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
            @click="onToggle('filter', $event)"
          >
            <span
              :class="
                isWorkspaceV2
                  ? 'i-ph-sliders-horizontal size-5'
                  : 'i-ph-funnel size-[18px]'
              "
              aria-hidden="true"
            />
            <span
              v-if="filterActive"
              data-testid="chat-list-filter-dot"
              class="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-n-blue-9"
              aria-hidden="true"
            />
          </button>
          <button
            v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
            type="button"
            data-testid="chat-list-shortcuts"
            class="inline-flex items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="[
              isWorkspaceV2 ? 'size-11' : 'size-9',
              shortcutsOpen
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
            ]"
            :aria-expanded="shortcutsOpen"
            aria-haspopup="dialog"
            :aria-label="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
            @click="onToggle('shortcuts', $event)"
          >
            <span
              :class="
                isWorkspaceV2 ? 'i-ph-funnel size-5' : 'i-ph-plus size-[18px]'
              "
              aria-hidden="true"
            />
          </button>
          <template v-if="hasAppliedFilters && !hasActiveFolders">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
              :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
              icon="i-ph-x-circle"
              ruby
              faded
              xs
              @click="emit('resetFilters')"
            />
          </template>
          <!-- Só editar fica no cabeçalho, com alvo de 44px: excluir foi para
               dentro do editor, longe de um toque errado no celular. -->
          <NextButton
            v-if="hasActiveFolders"
            id="toggleConversationFilterButton"
            v-tooltip.bottom="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            :aria-label="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-ph-pencil-simple"
            slate
            ghost
            sm
            class="!h-11 !w-11"
            data-testid="chat-list-edit-folder"
            @click="emit('filtersModal')"
          />
        </div>
      </div>

      <div class="min-w-0 pb-2.5" :class="CONVERSATION_COLUMN_INSET_CLASS">
        <div class="relative min-w-0 w-full">
          <span
            class="pointer-events-none absolute inset-y-0 flex items-center text-n-slate-10 ltr:left-3.5 rtl:right-3.5"
          >
            <span class="i-ph-magnifying-glass size-4" />
          </span>
          <input
            v-model="localSearchQuery"
            data-testid="conversation-list-search-input"
            type="search"
            :aria-label="$t('CHAT_LIST.SEARCH.INPUT')"
            :placeholder="$t('CHAT_LIST.SEARCH.INPUT')"
            class="reset-base !mb-0 min-w-0 h-[38px] w-full appearance-none rounded-full border-0 bg-n-alpha-2 py-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:bg-n-solid-2 focus:ring-1 focus:ring-n-brand ltr:pl-10 ltr:pr-9 rtl:pr-10 rtl:pl-9 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            @input="onSearchInput"
          />
          <button
            v-if="localSearchQuery"
            data-testid="conversation-list-search-clear"
            type="button"
            class="absolute inset-y-0 flex items-center px-1 text-n-slate-9 hover:text-n-slate-12 ltr:right-2.5 rtl:left-2.5"
            :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
            @click="clearSearchQuery"
          >
            <span class="i-ph-x size-4" />
          </button>
          <ChatListSearchOverlay :search-query="localSearchQuery" />
        </div>
      </div>
    </template>
  </div>
</template>
